import { useEffect, useState } from "react";
import axios from "axios";
import HorizontalBenchmarkChart from "./HorizontalBenchmarkChart";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api`;
const CALCULATED_VALUES_URL = `${API_BASE_URL}/kpi/calculated_values`;

type SelectedKpiRow = {
  category: string;
  kpi: string;
};

type CalculatedKpiRow = {
  kpi_category: string;
  kpi_name: string;
  [key: string]: unknown;
};

type CalculatedKpiRowWithMeta = CalculatedKpiRow & {
  kpi_id: string;
  company_id: number;
};

type CompanyBenchMarkingSummaryProps = {
  compareCompanyView: boolean;
};

const getStoredSelectedKpiRows = (): SelectedKpiRow[] => {
  try {
    const stored = localStorage.getItem("selectedKpiRows");
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getStoredCompanyIds = (): number[] => {
  try {
    const stored = localStorage.getItem("filteredCompanyIds");
    const parsedIds = stored ? JSON.parse(stored) : [];

    if (Array.isArray(parsedIds) && parsedIds.length > 0) {
      return [...new Set(parsedIds)].filter(
        (id): id is number => Number.isInteger(id),
      );
    }
  } catch {
    // Ignore malformed localStorage values and use fallback IDs.
  }

  return Array.from({ length: 14 }, (_, index) => index + 1);
};

function CompanyBenchMarkingSummary({ compareCompanyView }: CompanyBenchMarkingSummaryProps) {
  const company = localStorage.getItem("uploadedTempCompanyName") || "Selected Company";
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [calculatedData, setCalculatedData] = useState<CalculatedKpiRowWithMeta[]>([]);
  const [selectedKpiRows] = useState<SelectedKpiRow[]>(getStoredSelectedKpiRows);

  useEffect(() => {
    if (company) {
      void fetchData();
    }
  }, [company]);

  const fetchData = async (): Promise<void> => {
    setLoading(true);

    try {
      const companyIds = getStoredCompanyIds();
      const calculatedResponses = await Promise.all(
        companyIds.map((companyId) =>
          axios.get(CALCULATED_VALUES_URL, {
            params: { company_id: companyId },
          }),
        ),
      );

      const combinedCalculatedData = calculatedResponses.flatMap(
        (calculatedResponse, companyIndex) => {
          const rows: CalculatedKpiRow[] = Array.isArray(calculatedResponse.data?.rows)
            ? calculatedResponse.data.rows
            : [];
          const companyId = companyIds[companyIndex];

          return rows.map((kpi, rowIndex) => ({
            ...kpi,
            kpi_id: `${companyId}-${rowIndex + 1}`,
            company_id: companyId,
          }));
        },
      );

      setCalculatedData(combinedCalculatedData);

      if (company) {
        await fetchExecutiveSummary();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setCalculatedData([]);
    }

    setLoading(false);
  };

  const fetchExecutiveSummary = async (): Promise<void> => {
    setLoadingSummary(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/general-observation`, {
        params: { company, environment: "temp" },
      });
      setExecutiveSummary(response.data.observation || "No summary available.");
    } catch (error) {
      console.error("Error fetching executive summary:", error);
      setExecutiveSummary(
        "Unable to generate executive summary. Please try again.",
      );
    }

    setLoadingSummary(false);
  };

  const visibleCalculatedData =
    selectedKpiRows.length > 0
      ? calculatedData.filter((item) =>
          selectedKpiRows.some(
            (row) =>
              row.category === item.kpi_category && row.kpi === item.kpi_name,
          ),
        )
      : calculatedData;

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } min-h-screen w-full relative overflow-hidden`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl"></div>
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl"></div>
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-indigo-100/60 blur-3xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-0 pb-10 overflow-visible">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div
              className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-700 border-t-indigo-700"
              aria-label="Loading"
            ></div>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Loading data...
            </p>
          </div>
        )}

        {!loading && visibleCalculatedData.length > 0 && (
          <>
            <div className="bg-white/95 dark:bg-gray-900/95 border border-indigo-100 dark:border-indigo-800/60 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-extrabold text-indigo-900 dark:text-indigo-200">
                  Executive Summary
                </h3>
                <button
                  onClick={() => setIsEditingSummary(!isEditingSummary)}
                  className="text-sm px-5 py-2.5 rounded-xl font-semibold bg-indigo-700 text-white hover:bg-indigo-800 hover:shadow-lg hover:shadow-indigo-300/40 transition-all"
                >
                  {isEditingSummary ? "Save" : "Edit"}
                </button>
              </div>
              {loadingSummary ? (
                <div className="text-center py-4 text-gray-600 dark:text-gray-400">
                  Generating executive summary...
                </div>
              ) : isEditingSummary ? (
                <textarea
                  value={executiveSummary}
                  onChange={(e) => setExecutiveSummary(e.target.value)}
                  className="w-full min-h-[120px] px-4 py-3 border border-indigo-200 dark:border-indigo-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-y"
                  placeholder="Enter executive summary for the company..."
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap rounded-xl border border-indigo-100 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-indigo-900/20 px-4 py-3">
                  {executiveSummary || "No summary available. Click Edit to add one."}
                </div>
              )}
            </div>

            <HorizontalBenchmarkChart
              compareCompanyView={compareCompanyView}
              calculatedData={visibleCalculatedData}
            />
          </>
        )}

        {!loading && visibleCalculatedData.length === 0 && (
          <div className="text-center py-12 text-lg text-gray-600 dark:text-gray-400">
            No data available for the selected criteria.
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyBenchMarkingSummary;